import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, ViewChild, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { WorkflowProgressService } from '../../../core/services/workflow-progress.service';
import { AccessibilityStateService } from '../../../core/services/accessibility-state.service';

export type WorkflowStep = 'instrument' | 'focalization' | 'geography' | 'problems' | 'actors' | 'objectives' | 'formulation';

@Component({
  selector: 'sr-psa-workflow-steps',
  standalone: true,
  imports: [RouterLink],
  templateUrl: './workflow-steps.component.html',
  styleUrl: './workflow-steps.component.css'
})
export class WorkflowStepsComponent implements OnChanges, AfterViewInit, OnDestroy {
  readonly progress = inject(WorkflowProgressService);
  readonly accessibility = inject(AccessibilityStateService);
  @Input({ required: true }) active!: WorkflowStep;
  @Input() readonly = false;
  @Input() commentsEnabled = true;
  @Input() adjustmentMode = false;
  @Input() commentsReadOnly = false;
  @Output() readonlyStepSelected = new EventEmitter<WorkflowStep>();
  @Output() commentSubmitted = new EventEmitter<{ step: WorkflowStep; message: string }>();
  @ViewChild('adjustmentComments') adjustmentComments?: ElementRef<HTMLElement>;
  commentOpenFor: WorkflowStep | null = null;
  footerOffset = 0;

  private readonly updateFloatingComments = () => {
    const panel = this.adjustmentComments?.nativeElement;
    const footer = document.querySelector('footer');
    const shouldFloat = window.innerWidth > 900 && window.innerWidth < 2000;
    if (!panel || !footer || !shouldFloat) { this.footerOffset = 0; return; }
    this.footerOffset = Math.max(0, Math.ceil(panel.getBoundingClientRect().bottom + this.footerOffset - footer.getBoundingClientRect().top + 16));
  };

  ngOnChanges(): void {
    if (this.adjustmentMode && this.commentsEnabled && !this.commentsReadOnly) this.commentOpenFor = this.active;
    if (this.readonly && this.commentOpenFor) this.commentOpenFor = this.active;
    queueMicrotask(this.updateFloatingComments);
  }

  ngAfterViewInit(): void {
    window.addEventListener('scroll', this.updateFloatingComments, true);
    window.addEventListener('resize', this.updateFloatingComments);
    this.updateFloatingComments();
  }

  ngOnDestroy(): void {
    window.removeEventListener('scroll', this.updateFloatingComments, true);
    window.removeEventListener('resize', this.updateFloatingComments);
  }

  toggleComments(step: WorkflowStep): void { this.commentOpenFor = this.commentOpenFor === step ? null : step; }
  submitComment(step: WorkflowStep, message: string): void {
    const text = message.trim();
    if (text) this.commentSubmitted.emit({ step, message: text });
  }
  selectStep(event: MouseEvent, step: WorkflowStep): void { if (!this.readonly) return; event.preventDefault(); this.readonlyStepSelected.emit(step); }
  labelFor(step: WorkflowStep): string { return { instrument: 'Instrumento', focalization: 'Focalización', geography: 'Información geográfica', problems: 'Problemas', actors: 'Actores', objectives: 'Objetivos', formulation: 'Formulación' }[step]; }
  commentFor(step: WorkflowStep): { title: string; message: string; followUp: string } {
    return {
      instrument: { title: 'Instrumento', message: 'Verifique que el instrumento seleccionado corresponde a la información registrada para el proyecto.', followUp: 'Confirme la selección del instrumento antes de finalizar la revisión.' },
      focalization: { title: 'Focalización', message: 'Revise los datos de focalización, las modalidades y la autoridad ambiental competente.', followUp: 'Valide que los anexos de focalización correspondan al proyecto.' },
      geography: { title: 'Información geográfica', message: 'Compruebe el área validada, los predios asociados y los archivos geográficos registrados.', followUp: 'Verifique que la delimitación y las capas reportadas sean consistentes.' },
      problems: { title: 'Problemas', message: 'Revise que el problema central y las relaciones de causas y efectos estén correctamente descritos.', followUp: 'Confirme que las causas y efectos corresponden al árbol de problemas.' },
      actors: { title: 'Actores', message: 'Verifique los actores registrados y el documento de caracterización asociado.', followUp: 'Revise que el rol y el tipo de cada actor sean los adecuados.' },
      objectives: { title: 'Objetivos', message: 'Revise el objetivo central y la relación entre objetivos específicos y causas directas.', followUp: 'Confirme que las actividades propuestas responden a las causas priorizadas.' },
      formulation: { title: 'Formulación', message: 'Revise la información registrada en las historias de caracterización, incentivo, financiación y plan operativo.', followUp: 'Verifique los valores, anexos y soportes de cada historia de usuario.' }
    }[step];
  }
}
